import { useState, useEffect, useRef } from "react";
import { Plus, X, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/App";
import ImageCropper from "./ImageCropper";
import { MUSIC_LIBRARY, getMusicPlayer } from "./MusicLibrary";
import StoryViewer from "./StoryViewer";
import StoryStudio from "./story/StoryStudio";
import { prepareImage } from "./story/prepareMedia";

// Stories/Mood component - 24h visibility
export const StoriesBar = ({ currentUser, deepLinkStoryId }) => {
  const [stories, setStories] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [viewerSession, setViewerSession] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState("none");
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [studio, setStudio] = useState(null); // { file, kind }

  const fileInputRef = useRef(null);
  const musicPlayerRef = useRef(getMusicPlayer());

  const fetchStories = async () => {
    try {
      const response = await api.get('/stories');
      setStories(response.data.stories || []);
      setMyStories(response.data.my_stories || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  };

  useEffect(() => {
    fetchStories();
    const interval = setInterval(fetchStories, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const deepLinkDoneRef = useRef(false);
  useEffect(() => {
    if (!deepLinkStoryId || deepLinkDoneRef.current) return;
    if (stories.length === 0 && myStories.length === 0) return;
    const groups = [];
    if (myStories.length > 0) groups.push({ user: currentUser, stories: myStories, isMine: true });
    stories.forEach((s) => groups.push({ ...s, isMine: false }));
    for (let gi = 0; gi < groups.length; gi++) {
      const si = groups[gi].stories.findIndex((s) => s.story_id === deepLinkStoryId);
      if (si >= 0) {
        deepLinkDoneRef.current = true;
        setViewerSession({ groups, initialUserIndex: gi, initialStoryIndex: si });
        return;
      }
    }
    deepLinkDoneRef.current = true;
  }, [deepLinkStoryId, stories, myStories, currentUser]);

  const hasUnseen = (userStories) => userStories.stories.some((s) => !s.viewed);

  // Unseen moods first; fully-viewed ones go to the end of the bar
  const sortedStories = [...stories].sort((a, b) => Number(hasUnseen(b)) - Number(hasUnseen(a)));

  const openViewer = (startKey) => {
    const groups = [];
    if (myStories.length > 0) {
      groups.push({ user: currentUser, stories: myStories, isMine: true });
    }
    sortedStories.forEach((s) => groups.push({ ...s, isMine: false }));
    if (groups.length === 0) return;
    const idx = startKey === "mine"
      ? 0
      : groups.findIndex((g) => !g.isMine && g.user.user_id === startKey);
    if (idx < 0) return;
    // Start from the first unseen story so no mood gets missed
    const firstUnseen = groups[idx].stories.findIndex((s) => !s.viewed);
    setViewerSession({ groups, initialUserIndex: idx, initialStoryIndex: Math.max(0, firstUnseen) });
  };

  const handleFileSelect = async (e) => {
    const input = e.target;
    const file = input.files[0];
    if (!file) return;
    input.value = ''; // reset subito: il file resta in memoria

    if (file.type.startsWith('image/') || /\.(hei[cf]|jpe?g|png|webp)$/i.test(file.name)) {
      // Nessun limite di MB: la foto viene ridotta a misura storia prima dell'editor
      toast.loading("Preparo la foto...", { id: "prep" });
      const prepared = await prepareImage(file);
      toast.dismiss("prep");
      if (!prepared) {
        toast.error("Non riesco a leggere questa immagine, prova con un'altra");
        return;
      }
      setStudio({ file: prepared, kind: "image" });
    } else {
      // Video: qualsiasi dimensione, il server comprime a 720p e taglia a 30s
      if (file.size > 150 * 1024 * 1024) {
        toast.error("Video troppo grande (max 150MB)");
        return;
      }
      setStudio({ file, kind: "video" });
    }
  };

  const handleCroppedImage = (croppedBlob) => {
    // After crop → ADE Story Studio (testo, adesivi, interattivi, disegno, filtri, musica)
    const f = croppedBlob instanceof File
      ? croppedBlob
      : new File([croppedBlob], `story_${Date.now()}.jpg`, { type: croppedBlob.type || "image/jpeg" });
    setShowCropper(false);
    setCropperImage(null);
    setStudio({ file: f, kind: "image" });
  };

  const handleUploadStory = async () => {
    if (!previewFile) return;

    setUploading(true);
    try {
      const formData = new FormData();

      // Ensure the file has a proper filename
      let fileToUpload = previewFile;
      if (previewFile instanceof Blob && !previewFile.name) {
        fileToUpload = new File([previewFile], `story_${Date.now()}.jpg`, { type: 'image/jpeg' });
      }

      formData.append('file', fileToUpload);
      if (selectedMusic && selectedMusic !== "none") {
        formData.append('music_id', selectedMusic);
      }

      await api.post('/stories', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setShowUploadModal(false);
      setPreviewImage(null);
      setPreviewFile(null);
      setSelectedMusic("none");
      musicPlayerRef.current.stop();
      fetchStories();
      toast.success("Mood pubblicato!");
    } catch (error) {
      console.error('Story upload error:', error);
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '/images/default-avatar.png';
    if (url === '/images/default-avatar.png') return url;
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  return (
    <>
      {/* Stories Bar */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
          {/* Add Story Button — badge + never clipped (sits on outer wrapper, inside circle bounds) */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="relative w-[62px] h-[62px]">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-full rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center hover:from-gray-200 hover:to-gray-300 transition-all overflow-hidden"
                data-testid="add-story-btn"
              >
                <img
                  src={getImageUrl(currentUser?.picture)}
                  alt="Me"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                />
              </button>
              <div
                className="absolute bottom-0 right-0 w-[22px] h-[22px] rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 flex items-center justify-center border-2 border-white dark:border-black pointer-events-none z-10"
                data-testid="add-story-plus-badge"
              >
                <Plus className="w-3 h-3 text-white" strokeWidth={3.5} />
              </div>
            </div>
            <span className="text-[10px] text-gray-500 font-medium w-16 truncate text-center leading-tight">
              {myStories.length > 0 ? "Il tuo mood" : "Mood"}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* My Stories */}
          {myStories.length > 0 && (
            <div
              className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
              onClick={() => openViewer("mine")}
              data-testid="my-stories-bubble"
            >
              <div className="w-[62px] h-[62px] rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 p-[3px]">
                <div className="w-full h-full rounded-full bg-white p-[2px]">
                  <div className="w-full h-full rounded-full overflow-hidden">
                    <img
                      src={getImageUrl(currentUser?.picture)}
                      alt="Me"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                    />
                  </div>
                </div>
              </div>
              <span className="text-[10px] text-gray-700 font-medium truncate w-16 text-center">Tu</span>
            </div>
          )}

          {/* Other Users' Stories — unseen first with animated fuchsia ring */}
          {sortedStories.map((userStories) => {
            const unseen = hasUnseen(userStories);
            return (
              <div
                key={userStories.user.user_id}
                className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
                onClick={() => openViewer(userStories.user.user_id)}
                data-testid={`story-bubble-${userStories.user.user_id}`}
                data-unseen={unseen}
              >
                <div className="relative w-[62px] h-[62px]">
                  <div className={`absolute inset-0 rounded-full ${unseen ? "mood-ring-spin" : "mood-ring-seen"}`} />
                  <div className="absolute inset-[3px] rounded-full p-[2px]" style={{ background: "var(--bg-primary)" }}>
                    <div className="w-full h-full rounded-full overflow-hidden">
                      <img
                        src={getImageUrl(userStories.user?.picture)}
                        alt={userStories.user?.name}
                        className={`w-full h-full object-cover ${unseen ? "" : "opacity-80"}`}
                        onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                      />
                    </div>
                  </div>
                </div>
                <span className={`text-[10px] font-medium truncate w-16 text-center ${unseen ? "text-gray-700" : "text-gray-400"}`}>
                  {userStories.user.name?.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Preview Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => {
        setShowUploadModal(open);
        if (!open) { setSelectedMusic("none"); musicPlayerRef.current.stop(); }
      }}>
        <DialogContent className="sm:max-w-md bg-black border-0 p-0 overflow-hidden">
          <DialogTitle className="sr-only">Anteprima Mood</DialogTitle>
          {previewImage && (
            <div className="relative aspect-[9/16] max-h-[80vh]">
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-full object-contain bg-black"
              />

              {/* Close button */}
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setPreviewImage(null);
                  setPreviewFile(null);
                  setSelectedMusic("none");
                  musicPlayerRef.current.stop();
                }}
                className="absolute top-4 left-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Music selector button */}
              <button
                onClick={() => setShowMusicPicker(!showMusicPicker)}
                className={`absolute top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                  selectedMusic !== "none"
                    ? "bg-amber-500/90 text-white"
                    : "bg-black/50 text-white hover:bg-black/70"
                }`}
                data-testid="music-selector-btn"
              >
                <Music className="w-4 h-4" />
                <span className="text-xs font-medium">
                  {selectedMusic !== "none"
                    ? MUSIC_LIBRARY.find(m => m.id === selectedMusic)?.name || "Musica"
                    : "Musica"
                  }
                </span>
              </button>

              {/* Music picker overlay */}
              {showMusicPicker && (
                <div className="absolute top-16 right-4 left-4 bg-black/90 backdrop-blur-xl rounded-2xl p-3 max-h-[40vh] overflow-y-auto z-10">
                  <p className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-2 px-1">Scegli il mood</p>
                  <div className="grid grid-cols-2 gap-2">
                    {MUSIC_LIBRARY.map((track) => (
                      <button
                        key={track.id}
                        onClick={() => {
                          setSelectedMusic(track.id);
                          setShowMusicPicker(false);
                          if (track.id !== "none") {
                            musicPlayerRef.current.start(track.id);
                            setTimeout(() => musicPlayerRef.current.stop(), 3000);
                          } else {
                            musicPlayerRef.current.stop();
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all text-left ${
                          selectedMusic === track.id
                            ? "bg-white/20 ring-1 ring-white/40"
                            : "bg-white/5 hover:bg-white/10"
                        }`}
                        data-testid={`music-${track.id}`}
                      >
                        <span className="text-lg">{track.icon}</span>
                        <span className="text-white text-xs font-medium">{track.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Publish button */}
              <div className="absolute bottom-4 left-4 right-4">
                <Button
                  onClick={handleUploadStory}
                  disabled={uploading}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white font-medium"
                >
                  {uploading ? "Pubblicando..." : "Pubblica Mood"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Story Viewer — full rebuilt experience */}
      {viewerSession && (
        <StoryViewer
          groups={viewerSession.groups}
          initialUserIndex={viewerSession.initialUserIndex}
          initialStoryIndex={viewerSession.initialStoryIndex}
          onClose={() => { setViewerSession(null); fetchStories(); }}
          onStoriesChanged={fetchStories}
        />
      )}

      {/* ADE Story Studio */}
      {studio && (
        <StoryStudio
          file={studio.file}
          kind={studio.kind}
          onClose={() => setStudio(null)}
          onPublished={() => { setStudio(null); fetchStories(); }}
        />
      )}

      {/* Image Cropper for Stories */}
      <ImageCropper
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setCropperImage(null);
        }}
        imageSrc={cropperImage}
        onCropComplete={handleCroppedImage}
        aspectRatio={9/16}
        allowAspectChange={true}
        title="Ritaglia il tuo Mood"
        cropShape="rect"
      />

      {/* Full media editor for Stories */}
    </>
  );
};

export default StoriesBar;
